import React, { useState } from 'react';
import { Plus, Settings } from 'lucide-react';
import { CopyButton, DeleteButton } from './shared';
import { useLocale } from '../../contexts/LocaleContext';

const EMPTY_CRYPTO_FORM = { coin: '', network: '', address: '', label: '' };

export default function CryptoWalletsPanel({ wallets, onSave, onDelete }) {
  const { t } = useLocale();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_CRYPTO_FORM);
  const [editId, setEditId] = useState(null);

  const resetForm = () => {
    setForm(EMPTY_CRYPTO_FORM);
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (w) => {
    setForm({ coin: w.coin, network: w.network, address: w.address, label: w.label || '' });
    setEditId(w.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-2">
      {wallets.map((w) => (
        <div
          key={w.id}
          className="flex items-center gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)]">
              {w.coin}{' '}
              <span className="text-xs font-normal text-[var(--text-secondary)]">/ {w.network}</span>
              {w.label && (
                <span className="ml-1.5 text-xs text-[var(--text-tertiary)]">({w.label})</span>
              )}
            </p>
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-xs font-mono text-[var(--text-secondary)] truncate">{w.address}</p>
              <CopyButton text={w.address} />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => startEdit(w)}
              className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
            <DeleteButton onDelete={() => onDelete(w.id)} />
          </div>
        </div>
      ))}

      {showForm && (
        <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-primary)]">
            {editId ? t('plugins.editLabel') : t('plugins.newLabel')} {t('plugins.cryptoWallet')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder={t('plugins.coinPlaceholder')}
              value={form.coin}
              onChange={(e) => setForm({ ...form, coin: e.target.value })}
              className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
            />
            <input
              placeholder={t('plugins.networkPlaceholder')}
              value={form.network}
              onChange={(e) => setForm({ ...form, network: e.target.value })}
              className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
            />
            <input
              placeholder={t('plugins.walletAddressPlaceholder')}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)] col-span-2"
            />
            <input
              placeholder={t('plugins.labelOptionalPlaceholder')}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { onSave(editId, form); resetForm(); }}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs transition-smooth"
            >
              {editId ? t('plugins.saveLabel') : t('plugins.addLabel')}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth"
            >
              {t('plugins.cancelLabel')}
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('plugins.addCryptoWallet')}
        </button>
      )}
    </div>
  );
}